import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { AnimatePresence, motion } from "framer-motion";
import { Download } from "lucide-react";
import Lenis from "lenis";
import BottomNav from "@/components/BottomNav";
import AffiliateCloud from "@/components/AffiliateCloud";
import RaffleCloud from "@/components/RaffleCloud";
import HomePage from "@/pages/HomePage";
import ProductsPage from "@/pages/ProductsPage";
import InfoPage from "@/pages/InfoPage";
import AdminPage from "@/pages/AdminPage";
import BookingStatusPage from "@/pages/BookingStatusPage";
import StaffPage from "@/pages/StaffPage";
import PrivacyPage from "@/pages/PrivacyPage";
import AgendaPage from "@/pages/AgendaPage";
import { initTenant, isTenant, isTenantAdminPath } from "@/tenant";

initTenant();

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const APP_VERSION_KEY = "bl_app_version";

// Aviso visible de nueva versión: una píldora fija arriba que el usuario toca
// para actualizar. Comprueba al abrir la app y cada 60s. Se puede ocultar con la X
// (vuelve a aparecer solo si hay otra versión nueva).
function UpdateBanner() {
  const [newVersion, setNewVersion] = useState(null);
  const dismissedRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const check = async () => {
      try {
        const r = await fetch(`${BACKEND}/api/version`);
        const d = await r.json();
        const current = localStorage.getItem(APP_VERSION_KEY);
        if (current && current !== d.version) {
          if (alive && dismissedRef.current !== d.version) setNewVersion(d.version);
          return;
        }
        localStorage.setItem(APP_VERSION_KEY, d.version);
      } catch {}
    };
    check();
    const iv = setInterval(check, 60000);
    return () => {
      alive = false;
      clearInterval(iv);
    };
  }, []);

  if (!newVersion) return null;
  return (
    <div
      data-testid="update-banner"
      className="fixed inset-x-0 top-3 z-[80] flex justify-center px-4"
    >
      <div className="flex items-center gap-2.5 rounded-full border border-emerald-400/40 bg-zinc-900/95 py-1.5 pl-4 pr-1.5 shadow-[0_4px_24px_rgba(16,185,129,0.35)] backdrop-blur">
        <span className="text-[11px] font-bold text-emerald-300">
          ✨ Nuova versione disponibile
        </span>
        <button
          data-testid="update-now-btn"
          onClick={() => {
            localStorage.setItem(APP_VERSION_KEY, newVersion);
            window.location.reload();
          }}
          className="rounded-full bg-emerald-500 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-widest text-zinc-950 transition-colors hover:bg-emerald-400"
        >
          Aggiorna
        </button>
        <button
          data-testid="update-dismiss-btn"
          onClick={() => {
            dismissedRef.current = newVersion;
            setNewVersion(null);
          }}
          className="pr-2 text-xs text-zinc-500 transition-colors hover:text-white"
          aria-label="Chiudi"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

// Link corto /s/{code}: resuelve el slug del barbero y redirige a su app
function ShortRedirect({ code }) {
  const [err, setErr] = useState(false);
  useEffect(() => {
    fetch(`${BACKEND}/api/short/${code}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => window.location.replace(`/t/${d.slug}`))
      .catch(() => setErr(true));
  }, [code]);
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-zinc-950">
      <p className="text-sm text-zinc-500">
        {err ? "Link non valido." : "Apertura dell'app del salone…"}
      </p>
    </div>
  );
}

const PAGES = ["home", "categoria", "info"];

const variants = {
  enter: (dir) => ({ x: dir >= 0 ? 90 : -90, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir >= 0 ? -90 : 90, opacity: 0 }),
};

function App() {
  const [isAdmin] = useState(
    () => window.location.pathname.startsWith("/admin") || isTenantAdminPath()
  );
  const [isPrivacy] = useState(() => window.location.pathname.startsWith("/privacy"));
  const [shortCode] = useState(() => {
    const m = window.location.pathname.match(/^\/s\/([a-z0-9]{6})/);
    return m ? m[1] : null;
  });
  const [agendaToken] = useState(() => {
    const m = window.location.pathname.match(/^\/agenda\/([a-z0-9]+)/);
    return m ? m[1] : null;
  });
  const [staffToken] = useState(() => {
    const m = window.location.pathname.match(/^\/staff\/([a-f0-9]+)/);
    return m ? m[1] : null;
  });
  const [bookingId] = useState(() => {
    const m = window.location.pathname.match(/^\/cita\/([a-f0-9-]+)/);
    return m ? m[1] : null;
  });
  const [tab, setTab] = useState(0);
  const [dir, setDir] = useState(1);
  const [installEvt, setInstallEvt] = useState(null);
  const [showInstallInvite, setShowInstallInvite] = useState(false);
  const mainRef = useRef(null);

  const isIOS =
    typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone);

  useEffect(() => {
    if (isStandalone) return undefined;
    const dismissed = Number(localStorage.getItem("bl_install_dismiss") || 0);
    if (Date.now() - dismissed < 7 * 24 * 3600 * 1000) return undefined;
    const t = setTimeout(() => setShowInstallInvite(true), 4000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissInstallInvite = () => {
    localStorage.setItem("bl_install_dismiss", String(Date.now()));
    setShowInstallInvite(false);
  };

  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    const lenis = new Lenis({
      wrapper: el,
      content: el.firstElementChild || el,
      duration: 1.1,
      smoothWheel: true,
    });
    let rafId;
    const loop = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, [tab]);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallEvt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    const ping = () => {
      let d = localStorage.getItem("bl_device");
      if (!d) {
        d = crypto.randomUUID();
        localStorage.setItem("bl_device", d);
      }
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/app-install`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ device_id: d }),
      }).catch(() => {});
    };
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (standalone) ping();
    window.addEventListener("appinstalled", ping);
    return () => window.removeEventListener("appinstalled", ping);
  }, []);

  const goTo = (next) => {
    if (next === tab) return;
    setDir(next > tab ? 1 : -1);
    setTab(next);
  };

  const earlyPage = isAdmin ? (
    <AdminPage />
  ) : isPrivacy ? (
    <PrivacyPage />
  ) : shortCode ? (
    <ShortRedirect code={shortCode} />
  ) : agendaToken ? (
    <AgendaPage token={agendaToken} />
  ) : staffToken ? (
    <StaffPage token={staffToken} />
  ) : bookingId ? (
    <BookingStatusPage bookingId={bookingId} />
  ) : null;
  if (earlyPage)
    return (
      <>
        {earlyPage}
        <UpdateBanner />
      </>
    );

  return (
    <div className="min-h-[100dvh] bg-zinc-950 text-white">
      <div
        className="relative mx-auto h-[100dvh] w-full max-w-md overflow-hidden shadow-2xl"
      >
        <AnimatePresence mode="wait" custom={dir} initial={false}>
          <motion.main
            key={tab}
            ref={mainRef}
            custom={dir}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.28, ease: "easeOut" }}
            className="h-full overflow-y-auto px-6 pb-32 pt-6"
          >
            {tab === 0 && <HomePage goTo={goTo} />}
            {tab === 1 && <ProductsPage />}
            {tab === 2 && <InfoPage />}
          </motion.main>
        </AnimatePresence>
        <AnimatePresence>
          {showInstallInvite && (
            <motion.div
              data-testid="install-invite"
              initial={{ y: 90, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 90, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="fixed bottom-24 left-0 right-0 z-[60] mx-auto w-[92%] max-w-sm rounded-2xl border border-amber-400/30 bg-zinc-900/95 p-4 shadow-[0_8px_40px_rgba(0,0,0,0.6)] backdrop-blur"
            >
              <div className="flex items-start gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-400/15 text-amber-400">
                  <Download size={20} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-extrabold text-white">
                    Installa l&apos;app di Barber Latin
                  </p>
                  <p className="mt-0.5 text-xs leading-relaxed text-zinc-400">
                    Prenota più in fretta e ricevi le novità direttamente sul tuo telefono.
                  </p>
                </div>
                <button
                  data-testid="install-invite-close"
                  onClick={dismissInstallInvite}
                  className="shrink-0 text-zinc-500 transition-colors hover:text-white"
                >
                  ✕
                </button>
              </div>
              {installEvt ? (
                <button
                  data-testid="install-invite-go"
                  onClick={async () => {
                    installEvt.prompt();
                    setInstallEvt(null);
                    dismissInstallInvite();
                  }}
                  className="mt-3 h-11 w-full rounded-xl bg-amber-400 text-xs font-extrabold uppercase tracking-widest text-zinc-950 transition-colors hover:bg-amber-300"
                >
                  Installa ora — gratis
                </button>
              ) : isIOS ? (
                <p className="mt-3 rounded-xl bg-zinc-950/70 px-3 py-2 text-center text-xs text-zinc-300">
                  Tocca <span className="font-bold text-amber-300">Condividi</span> e poi{" "}
                  <span className="font-bold text-amber-300">&quot;Aggiungi a Home&quot;</span>
                </p>
              ) : (
                <p className="mt-3 rounded-xl bg-zinc-950/70 px-3 py-2 text-center text-xs text-zinc-300">
                  Dal menu del browser scegli{" "}
                  <span className="font-bold text-amber-300">&quot;Installa app&quot;</span> o{" "}
                  <span className="font-bold text-amber-300">&quot;Aggiungi a Home&quot;</span>
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
        {installEvt && !showInstallInvite && (
          <button
            data-testid="btn-install"
            onClick={async () => {
              installEvt.prompt();
              setInstallEvt(null);
            }}
            className="fixed bottom-32 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2 rounded-full bg-amber-400 px-5 py-2.5 text-xs font-extrabold uppercase tracking-widest text-zinc-950 shadow-[0_0_24px_rgba(251,191,36,0.35)] transition-[background-color,transform] duration-200 hover:bg-amber-300 active:scale-[0.97]"
          >
            <Download size={15} /> Installa l&apos;app
          </button>
        )}
        {!isTenant() && <RaffleCloud />}
        <AffiliateCloud />
        <BottomNav tab={tab} goTo={goTo} />
      </div>
      <UpdateBanner />
    </div>
  );
}

export default App;
